// Text input (DESIGN_SYSTEM §9.2): 16 px text, ≥ 44 px tall from padding + line height (never a
// fixed height that crops Devanagari), optional prefix/suffix and a grapheme counter.
import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx, graphemeCount } from "../lib";
import { isBlocked } from "../state";
import { useFieldContext } from "./Field";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  prefix?: ReactNode;
  /** Accessible name of the prefix when it is a symbol (e.g. "Nepali rupees"). */
  prefixLabel?: string;
  suffix?: ReactNode;
  /** Shows "{count} of {max} characters" counting graphemes. */
  maxGraphemes?: number;
  /** Merchant-entered content: use the Devanagari line height (§4.5). */
  userContent?: boolean;
  /** Ledger figures for numbers (§4.4). */
  ledger?: boolean;
}

export const controlFrame =
  "flex w-full items-stretch rounded-sm border bg-surface lg:rounded-xs text-fg transition-colors duration-(--ady-dur-quick) " +
  "focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-focus";

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    prefix,
    prefixLabel,
    suffix,
    maxGraphemes,
    userContent,
    ledger,
    className,
    id,
    value,
    readOnly,
    ...rest
  },
  ref,
) {
  const field = useFieldContext();
  const blocked = isBlocked(field?.availability);
  const invalid = field?.invalid ?? false;
  const count = typeof value === "string" && maxGraphemes ? graphemeCount(value) : null;
  return (
    <div className="flex flex-col gap-1">
      <div
        className={cx(
          controlFrame,
          invalid ? "border-danger-fg" : "border-control",
          blocked && "border-subtle bg-sunken text-fg-disabled",
        )}
      >
        {prefix ? (
          <span
            className="flex items-center border-e border-subtle px-3 text-md text-fg-secondary lg:text-sm"
            aria-hidden={prefixLabel ? undefined : true}
          >
            {prefixLabel ? <span className="ady-sr-only">{prefixLabel}</span> : null}
            <span aria-hidden="true">{prefix}</span>
          </span>
        ) : null}
        <input
          ref={ref}
          id={id ?? field?.id}
          value={value}
          readOnly={readOnly || blocked}
          aria-invalid={invalid || undefined}
          aria-describedby={field?.describedBy}
          aria-required={field?.required || undefined}
          aria-disabled={blocked || undefined}
          data-content={userContent ? "user" : undefined}
          className={cx(
            // D-038: 36 px / 14 px on desktop; phones keep 44 px / 16 px (no iOS zoom on focus).
            "min-w-0 flex-1 bg-transparent px-3 py-2.5 text-md outline-none placeholder:text-fg-tertiary lg:py-1.5 lg:text-sm",
            ledger && "ady-ledger",
            className,
          )}
          {...rest}
        />
        {suffix ? <span className="flex items-center px-3 text-fg-secondary">{suffix}</span> : null}
      </div>
      {count !== null && maxGraphemes ? (
        <span
          className={cx(
            "self-end text-xs",
            count > maxGraphemes ? "text-danger-fg" : "text-fg-tertiary",
          )}
        >
          {t("field.characters", { count, max: maxGraphemes })}
        </span>
      ) : null}
    </div>
  );
});
